import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import type { PhotoDuoProps } from './PhotoDuo.types';
import { Box } from '@withjoy/joykit';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { pxToRem } from '@withjoy/joykit/theme';
import { parseAspectRatio } from '@shared/utils/parseAspectRatio';
import { LOW_RES_RENDITION, OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';
import { GAP } from './PhotoDuo.constants';
import { getAspectRatio, getGridTemplateColumns, getScreenPartition } from './PhotoDuo.utils';
import { DuoPhotoRenderer } from './DuoPhotoRenderer';

const PHOTO_COUNT_TO_SHOW = 2;

export const PhotoDuo: FC<PhotoDuoProps> = memo(props => {
  const { photos, arrangement, showSkeleton, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW);

  const [containerDimensions, containerRef] = useSizeObserver();

  // we will be using the height of first photo for rest of the photos to avoid height mismatch
  const firstItemHeight = useMemo(() => {
    if (photos.length === 0) {
      return 0;
    }
    const firstItemAspectRatio = getAspectRatio(arrangement, 0);
    return (containerDimensions.width * getScreenPartition(arrangement, 0)) / parseAspectRatio(firstItemAspectRatio);
  }, [arrangement, containerDimensions.width, photos.length]);

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <Box
      display="grid"
      ref={containerRef}
      gridTemplateColumns={{ _: getGridTemplateColumns(arrangement), xs: getGridTemplateColumns(arrangement) }}
      gap={pxToRem(GAP)}
      width="100vw"
      position="relative"
      left="calc(-50vw + 50%)"
      overflow="hidden"
    >
      {filledPhotos.map((photo, index) => (
        <SsrLazyScreen
          key={photo.id}
          src={addRendition({ url: photo.photoUrl, renditionSize: LOW_RES_RENDITION })}
          width="100%"
          height="100%"
          aspectRatio={getAspectRatio(arrangement, index)}
          showSkeleton={showSkeleton}
        >
          {() =>
            photo.photoUrl ? (
              <DuoPhotoRenderer
                photo={photo}
                arrangement={arrangement}
                index={index}
                containerDimensions={containerDimensions}
                firstItemHeight={firstItemHeight}
                photoRenderer={OptimizedEventGuestSitePhoto}
              />
            ) : (
              <Box
                width="100%"
                height="100%"
                maxHeight={firstItemHeight ? pxToRem(firstItemHeight) : 'unset'}
                aspectRatio={getAspectRatio(arrangement, index)}
                backgroundColor="lightgray"
                key={photo.id}
              />
            )
          }
        </SsrLazyScreen>
      ))}
    </Box>
  );
});

PhotoDuo.displayName = 'PhotoDuo';
